import React, { FormEventHandler, useEffect, useState } from 'react'
import {
  resolveCatchMessage,
  storeConfigSelector,
  TCatchMessage,
  useLoadingContext,
  useToastContext,
} from '@segway/core'
import { useAppSelector } from '@segway/core/src/store/hooks'

import { Button } from '@/ui'

interface OpenPayCreditCardProps {
  cartPrices?:
    | {
        grand_total?: {
          value?: number | null
        } | null
      }
    | null
    | undefined
  onTokenCreated: (tokenId: string, deviceId: string, installments: number) => void
}

interface MsiRule {
  key: number
  value: number
}

const OpenPayCreditCard = ({ cartPrices, onTokenCreated }: OpenPayCreditCardProps) => {
  const [card, setCard] = useState({
    holder_name: '',
    card_number: '',
    expiration_month: '',
    expiration_year: '',
    cvv2: '',
  })
  const [installments, setInstallments] = useState<number>(0)
  const [useInstallment, setUseInstallment] = useState(false)
  const [showInstallment, setShowInstallment] = useState(false)
  const [installmentOptions, setInstallmentOptions] = useState<number[]>([])
  const [holding, setHolding] = useState(false)
  const loading = useLoadingContext()
  const toast = useToastContext()

  const storeConfig = useAppSelector(storeConfigSelector)

  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    try {
      if (storeConfig?.open_pay) {
        window.OpenPay?.setId?.(storeConfig?.open_pay?.id)
        window.OpenPay?.setApiKey(storeConfig?.open_pay?.public_key)
        window.OpenPay?.setSandboxMode(storeConfig?.open_pay?.is_sandbox)

        setUseInstallment(!!storeConfig?.open_pay?.installment_enable)

        const interestFree = storeConfig?.open_pay?.interest_free || ''
        if (interestFree) {
          // 处理 installment_options
          const rawRules = storeConfig?.open_pay?.msi_min_rules
          const msiMinRules: MsiRule[] = Array.isArray(rawRules)
            ? rawRules.map((r) => ({
                key: Number(r?.key),
                value: Number(r?.value),
              }))
            : []

          if (!Array.isArray(msiMinRules)) {
            setShowInstallment(false)
            return
          }

          const cartTotal = cartPrices?.grand_total?.value ?? 0
          const interestArray = interestFree
            .split(',')
            .map((n) => parseInt(n, 10))
            .filter(Boolean)

          if (msiMinRules.length === 0) {
            setInstallmentOptions(interestArray)
            return
          }

          const availableInstallments = interestArray.filter((msi) => {
            const rule = msiMinRules.find((r) => r.key === msi)
            return !rule || cartTotal >= rule.value
          })

          if (availableInstallments.length === 0) {
            setUseInstallment(false)
            return
          }

          setInstallmentOptions(availableInstallments)
        }
      }
    } catch (error) {
      console.warn('OpenPay Init Error:', error)
      toast.show({
        icon: 'fail',
        content: 'La inicialización de Openpay falló, actualice la página!',
      })
    }
  }, [storeConfig, cartPrices, toast])

  const validateField = (name: string, value: string) => {
    let error = ''

    switch (name) {
      case 'holder_name':
        if (!value.trim()) {
          error = 'Card holder name is required'
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
          error = 'El nombre del titular solo debe contener letras y espacios'
        }
        break
      case 'cvv2':
        if (!/^\d{3,4}$/.test(value)) {
          error = 'CVV inválido'
        }
        break
      default:
        break
    }

    // 更新 errors 对象
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }))
  }

  const validateExpiry = (month: string, year: string) => {
    let errorMonth = ''
    let errorYear = ''

    if (!/^\d{2}$/.test(month) || !/^\d{2}$/.test(year)) {
      if (!/^\d{2}$/.test(month)) {
        errorMonth = 'La fecha de expiración debe tener 2 dígitos en cada campo'
      }
      if (!/^\d{2}$/.test(year)) {
        errorYear = 'La fecha de expiración debe tener 2 dígitos en cada campo'
      }
    } else {
      const expMonth = parseInt(month, 10)
      const expYear = parseInt(year, 10)

      if (expMonth < 1 || expMonth > 12) {
        errorMonth = 'El mes debe estar entre 01 y 12'
      } else {
        // 当前年月
        const now = new Date()
        const currentMonth = now.getMonth() + 1
        const currentYear = now.getFullYear() % 100
        if (expYear < currentYear) {
          errorYear = 'El año no puede estar en el pasado'
        }
        if (expYear === currentYear && expMonth < currentMonth) {
          errorMonth = 'La fecha de expiración no puede estar en el pasado'
        }
      }
    }

    // 更新 errors 状态
    setErrors((prevErrors) => ({
      ...prevErrors,
      expiration_month: errorMonth,
      expiration_year: errorYear,
    }))
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    // setCard({ ...card, [e.target.name]: e.target.value });
    const { name, value } = e.target
    let newValue = value

    switch (name) {
      case 'card_number':
        // 移除所有非数字
        newValue = value.replace(/\D/g, '').slice(0, 30)
        break
      case 'expiration_month':
        // 只保留数字，并且最大两位
        newValue = value.replace(/\D/g, '').slice(0, 2)
        break
      case 'expiration_year':
        // 只保留数字，并且最大两位
        newValue = value.replace(/\D/g, '').slice(0, 2)
        break
      case 'cvv2':
        // 只保留数字，最大4位
        newValue = value.replace(/\D/g, '').slice(0, 4)
        break
      default:
        break
    }

    setCard((prevCard) => {
      const updatedCard = { ...prevCard, [name]: newValue }

      // 联动校验
      if (name === 'expiration_month' || name === 'expiration_year') {
        validateExpiry(updatedCard.expiration_month, updatedCard.expiration_year)
      } else {
        validateField(name, newValue) // 其他字段单独验证
      }

      return updatedCard
    })
  }

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    loading.show()
    setHolding(true)

    // check if any error or empty field
    const hasErrors = Object.values(errors).some((errorMsg) => errorMsg !== '')
    const emptyFields = Object.entries(card).filter(([, value]) => !value.trim())

    if (hasErrors || emptyFields.length > 0) {
      toast.show({
        icon: 'fail',
        content: 'Por favor, verifica la información de la tarjeta que ingresaste!',
      })
      loading.hide()
      setHolding(false)
      return
    }

    try {
      document.getElementById('device_session_id')?.remove()
      window.OpenPay.deviceData.setup('openpay-form', 'device_session_id')
      window.OpenPay.token.create(
        card,
        (response: Record<string, Record<string, string>>) => {
          const tokenId = response.data.id
          const deviceDocument = document.getElementById('device_session_id') as HTMLInputElement
          const deviceSessionId = deviceDocument ? deviceDocument.value : ''
          console.log('response:', response)
          onTokenCreated(tokenId, deviceSessionId, installments)
        },
        (error: Record<string, Record<string, string> | string>) => {
          loading.hide()
          console.log('Openpay Error 1:', error)
          if ((error.message as string).includes('Request error')) {
            toast.show({
              icon: 'fail',
              content:
                'Debido a restricciones de red(como restricciones de IP o región), elija otro método de pago.',
            })
            setHolding(false)
            return
          }
          toast.show({
            icon: 'fail',
            content:
              (error.data as Record<string, string>)?.description ||
              resolveCatchMessage(error as TCatchMessage),
          })
          setHolding(false)
        },
      )
    } catch (error) {
      loading.hide()
      console.log('Openpay Error 2:', error)
      toast.show({
        icon: 'fail',
        content: 'La inicialización de Openpay falló, actualice la página!',
      })
    }

    setHolding(false)
  }

  const handleUseInstallmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setShowInstallment(checked)

    if (!checked) {
      setInstallments(0)
    }
  }

  return (
    <form
      id="openpay-form"
      onSubmit={handleSubmit}
      className="mb-6 space-y-6 rounded-2xl bg-white px-6 pb-6 shadow-lg">
      <h2 className="text-xl font-semibold">Pagar con tarjeta</h2>

      <div className="space-y-4">
        {/* Holder Name */}
        <div>
          <label className="mb-1 block text-sm font-medium">Nombre del titular</label>
          <input
            type="text"
            name="holder_name"
            className="w-full rounded-xl border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Nombre del titular"
            onChange={handleChange}
            value={card.holder_name}
          />
          {errors.holder_name && <p className="mt-1 text-xs text-red-500">{errors.holder_name}</p>}
        </div>

        <div className="space-y-4">
          {/* Card Number */}
          <div>
            <label className="mb-1 block text-sm font-medium">Número de tarjeta</label>
            <input
              type="text"
              name="card_number"
              inputMode="numeric"
              className="w-full rounded-xl border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="•••• •••• •••• ••••"
              onChange={handleChange}
              value={card.card_number}
            />
            {errors.card_number && (
              <p className="mt-1 text-xs text-red-500">{errors.card_number}</p>
            )}
          </div>

          {/* Expiration */}
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium">Mes de expiración (MM)</label>
              <input
                type="text"
                name="expiration_month"
                className="w-full rounded-xl border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="MM"
                onChange={handleChange}
                value={card.expiration_month}
              />
              {errors.expiration_month && (
                <p className="mt-1 text-xs text-red-500">{errors.expiration_month}</p>
              )}
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium">Año de expiración (AA)</label>
              <input
                type="text"
                name="expiration_year"
                className="w-full rounded-xl border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="AA"
                onChange={handleChange}
                value={card.expiration_year}
              />
              {errors.expiration_year && (
                <p className="mt-1 text-xs text-red-500">{errors.expiration_year}</p>
              )}
            </div>
          </div>

          {/* Expiry Date & CVV */}
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium">CVV</label>
              <input
                type="text"
                name="cvv2"
                className="w-full rounded-xl border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="•••"
                onChange={handleChange}
                value={card.cvv2}
              />
              {errors.cvv2 && <p className="mt-1 text-xs text-red-500">{errors.cvv2}</p>}
            </div>
          </div>

          {/* Installment Selector */}
          <div className="space-y-2 pt-2">
            {useInstallment && (
              <label className="inline-flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                  checked={showInstallment}
                  onChange={handleUseInstallmentChange}
                />
                <span className="text-sm">Pago en cuotas</span>
              </label>
            )}

            {showInstallment && (
              <div>
                <label className="mb-1 block text-sm font-medium">
                  Seleccione el número máximo de MSI
                </label>
                <select
                  value={installments}
                  onChange={(e) => setInstallments(Number(e.target.value))}
                  className="w-full rounded-xl border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option key="" value="">
                    -- Sin MSI --
                  </option>
                  {installmentOptions.map((month) => (
                    <option key={month} value={month}>
                      {month} MSI
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* <input type="hidden" name="device_session_id" id="device_session_id" />*/}
      <Button type="submit" disabled={holding} className="mb-6 xl:mb-4">
        Realizar Pedido
      </Button>
    </form>
  )
}

export default OpenPayCreditCard
